import { useEffect, useMemo, useState } from "react";
import { fetchRequests, type TimeOffRequest } from "../lib/api";
import { TIME_OFF_TYPES, type TimeOffType } from "../lib/shared/timeOffTypes";
import { parseDateOnly } from "../lib/dateOnly";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function TeamCalendar() {
  const [requests, setRequests] = useState<TimeOffRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [visibleTypes, setVisibleTypes] = useState<Record<TimeOffType, boolean>>({
    vacation: true,
    sick: true,
    personal: true,
  });

  useEffect(() => {
    fetchRequests("calendar")
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load calendar"));
  }, []);

  const cells = useMemo(() => {
    const first = new Date(ym.y, ym.m, 1);
    const startDow = first.getDay();
    const daysIn = new Date(ym.y, ym.m + 1, 0).getDate();
    const list: (Date | null)[] = [];
    for (let i = 0; i < startDow; i++) list.push(null);
    for (let d = 1; d <= daysIn; d++) list.push(new Date(ym.y, ym.m, d));
    while (list.length % 7 !== 0) list.push(null);
    return list;
  }, [ym]);

  const visible = useMemo(
    () => (requests ?? []).filter((r) => visibleTypes[r.type]),
    [requests, visibleTypes]
  );

  function requestsOn(day: Date) {
    const d = startOfDay(day).getTime();
    return visible.filter((r) => {
      const s = startOfDay(parseDateOnly(r.start_date)).getTime();
      const e = startOfDay(parseDateOnly(r.end_date)).getTime();
      return d >= s && d <= e;
    });
  }

  function shiftMonth(delta: number) {
    setYm((prev) => {
      const nm = prev.m + delta;
      return { y: prev.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  }

  function isToday(d: Date): boolean {
    return startOfDay(d).getTime() === startOfDay(today).getTime();
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="di-h3">Team availability calendar</h1>
      </div>

      {error && <p className="form-error">{error}</p>}

      {requests === null ? (
        <p className="di-small">Loading…</p>
      ) : (
        <div className="di-card">
          <div className="cal-toolbar">
            <div className="cal-nav">
              <button className="di-btn di-btn-outline di-btn-small" onClick={() => shiftMonth(-1)}>
                ‹
              </button>
              <h2 className="cal-month-title">
                {MONTHS[ym.m]} {ym.y}
              </h2>
              <button className="di-btn di-btn-outline di-btn-small" onClick={() => shiftMonth(1)}>
                ›
              </button>
              <button
                className="di-btn di-btn-outline di-btn-small"
                onClick={() => setYm({ y: today.getFullYear(), m: today.getMonth() })}
              >
                Today
              </button>
            </div>
            <div className="cal-type-filters">
              {(Object.keys(TIME_OFF_TYPES) as TimeOffType[]).map((key) => (
                <button
                  key={key}
                  className="cal-type-toggle"
                  style={{ opacity: visibleTypes[key] ? 1 : 0.4 }}
                  onClick={() => setVisibleTypes((prev) => ({ ...prev, [key]: !prev[key] }))}
                >
                  <span className="cal-dot" style={{ background: TIME_OFF_TYPES[key].color }} />
                  {TIME_OFF_TYPES[key].label}
                </button>
              ))}
            </div>
          </div>

          <div className="cal-scroll">
            <div className="cal-grid-wrap">
              <div className="cal-head">
                {DOW.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="cal-grid">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} className="cal-cell cal-cell-out" />;
                  const dow = day.getDay();
                  const dayRequests = requestsOn(day);
                  return (
                    <div
                      key={i}
                      className={`cal-cell${isToday(day) ? " cal-cell-today" : ""}${
                        dow === 0 || dow === 6 ? " cal-cell-weekend" : ""
                      }`}
                    >
                      <div className="cal-date">{day.getDate()}</div>
                      {dayRequests.slice(0, 3).map((r) => {
                        const t = TIME_OFF_TYPES[r.type];
                        const pending = r.status === "pending";
                        return (
                          <div
                            key={r.id}
                            className="cal-chip"
                            title={`${r.person_name ?? r.person_email} · ${t.label}${pending ? " (pending)" : ""}`}
                            style={
                              pending
                                ? { color: "var(--fg3)", border: `1px dashed ${t.color}` }
                                : { background: t.color, color: "var(--ink-inverse)" }
                            }
                          >
                            {(r.person_name ?? r.person_email).split(" ")[0]}
                          </div>
                        );
                      })}
                      {dayRequests.length > 3 && (
                        <div className="cal-more">+{dayRequests.length - 3} more</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="cal-legend">
            <span>
              <span className="cal-legend-swatch cal-legend-confirmed" /> Confirmed
            </span>
            <span>
              <span className="cal-legend-swatch cal-legend-pending" /> Pending approval
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
